import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  Input,
  output,
  signal,
} from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { MatDividerModule } from '@angular/material/divider';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { QuotationsService } from '@pages/quotations';
import { switchMap } from 'rxjs';

import { VOneIconComponent } from '@shared-components';
import { TooltipDirective } from '@shared-directives';
import { IconType } from '@shared-enums';
import { CardCode, CardState, Note } from '@shared-models';
import { getRelatedNotes$, isPortfolioCardState } from '@shared-utils';

import { AlertService } from '../../../services/alert/alert.service';

@Component({
  selector: 'v-one-card-body',
  imports: [
    CurrencyPipe,
    DatePipe,
    MatDividerModule,
    TranslatePipe,
    forwardRef(() => VOneIconComponent),
    TooltipDirective,
  ],
  templateUrl: './card-body.component.html',
  styleUrl: './card-body.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardBodyComponent {
  protected readonly iconType = IconType;

  private readonly translate = inject(TranslateService);
  private readonly alertService = inject(AlertService);
  private readonly quotationsService = inject(QuotationsService);

  readonly idInvoice = input<number>();

  readonly notesDetailClick = output<Note[]>();

  protected readonly isPortfolioCard = computed(() =>
    isPortfolioCardState(this.state()),
  );

  private readonly portfolioInvoiceId = computed(() =>
    this.isPortfolioCard() ? this.idInvoice() : undefined,
  );

  protected readonly relatedNotes = toSignal(
    toObservable(this.portfolioInvoiceId).pipe(
      switchMap((id) => getRelatedNotes$(this.quotationsService, id)),
    ),
    { initialValue: [] as Note[] },
  );

  protected readonly notesSummary = computed(() =>
    this.relatedNotes().reduce(
      (acc, note) => {
        const amount = Math.abs(note.total ?? 0);
        if (note.tipo_nota === 'Credito') {
          acc.creditCount++;
          acc.netTotal -= amount;
        } else {
          acc.debitCount++;
          acc.netTotal += amount;
        }
        return acc;
      },
      { creditCount: 0, debitCount: 0, netTotal: 0 },
    ),
  );

  protected readonly codes = signal<CardCode[]>([]);
  protected readonly client = signal<string>('');
  protected readonly clientLabelKey = signal<string>('card.labels.client');
  protected readonly address = signal<string>('');
  protected readonly creationDate = signal<Date | null>(null);
  protected readonly dueDate = signal<Date | null>(null);
  protected readonly total = signal<number | null>(null);
  protected readonly originalTotal = signal<number | null>(null);
  protected readonly currency = signal<string>('USD');
  protected readonly state = signal<CardState>('order');
  protected readonly pendingAmount = signal<number | null>(null);

  @Input() set cardCodes(value: CardCode[]) {
    this.codes.set(value);
  }

  @Input() set cardClient(value: string) {
    this.client.set(value);
  }

  @Input() set cardClientLabel(value: string | undefined) {
    this.clientLabelKey.set(value || 'card.labels.client');
  }

  @Input() set cardAddress(value: string) {
    this.address.set(value);
  }

  @Input() set cardCreationDate(value: Date | undefined) {
    this.creationDate.set(value || null);
  }

  @Input() set cardDueDate(value: Date | undefined) {
    this.dueDate.set(value || null);
  }

  // A settled document owes zero, which is a figure worth showing, so this
  // keeps 0 instead of reading it as "no total".
  @Input() set cardTotal(value: number | undefined) {
    this.total.set(value ?? null);
  }

  @Input() set cardOriginalTotal(value: number | undefined) {
    this.originalTotal.set(value ?? null);
  }

  @Input() set cardCurrency(value: string | undefined) {
    this.currency.set(value || 'USD');
  }

  @Input() set cardState(value: CardState) {
    this.state.set(value);
  }

  @Input() set cardPendingAmount(value: number | undefined) {
    this.pendingAmount.set(value ?? null);
  }

  // The totals arrive already adjusted by the caller, which takes them from
  // the server. Re-applying the notes summary here would subtract them twice.
  protected readonly formattedTotal = computed(() => this.total());

  protected readonly formattedCreationDate = computed(() => {
    return this.creationDate();
  });

  protected readonly formattedDueDate = computed(() => {
    return this.dueDate();
  });

  protected readonly dueDateStatus = computed<{
    message: string;
    params?: { days: number };
    variant: 'warning' | 'danger';
    overdueDate?: string;
  } | null>(() => {
    if (!this.isPortfolioCard()) return null;

    const due = this.dueDate();
    if (!due) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);

    const diffDays = Math.round(
      (dueDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays < 0) {
      return {
        message: 'card.dueDate.overdue',
        variant: 'danger',
        overdueDate: CardBodyComponent.formatDate(due),
      };
    }
    if (diffDays === 0) {
      return { message: 'card.dueDate.dueToday', variant: 'warning' };
    }
    if (diffDays <= 10) {
      return {
        message: 'card.dueDate.dueInDays',
        params: { days: diffDays },
        variant: 'warning',
      };
    }
    return null;
  });

  private static formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  }

  protected readonly hasNotes = computed(() => this.relatedNotes().length > 0);

  protected readonly hasNotesAdjustment = computed(() => {
    const original = this.originalTotal();
    return original !== null && original !== this.total();
  });

  // Same reason as formattedTotal: the caller already netted the notes out.
  protected readonly adjustedPendingAmount = computed(() => {
    const pending = this.pendingAmount();
    if (pending === null) return null;

    return Math.max(0, pending);
  });

  protected isHighlighted(code: CardCode): boolean {
    const invoiceStates: CardState[] = [
      'invoice-without-notes',
      'invoice-with-notes',
    ];
    const deliveryState: CardState = 'delivery';

    if (code.label.toLowerCase() !== 'invoice') {
      return false;
    }

    if (this.state() === deliveryState) {
      return true;
    }

    if (!invoiceStates.includes(this.state())) {
      return false;
    }

    const hasCufe = (code.value?.length ?? 0) >= 50;
    if (hasCufe) {
      return true;
    }

    const totalCodes = this.codes().length;
    if (totalCodes === 2) {
      return true;
    }

    return false;
  }

  protected isCufeCode(code: CardCode): boolean {
    return (
      this.isHighlighted(code) &&
      !!code.value &&
      code.value.trim() !== '' &&
      code.value.length >= 50
    );
  }

  protected handleNotesDetailClick(): void {
    this.notesDetailClick.emit(this.relatedNotes());
  }

  protected copyToClipboard(code: string): void {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      this.alertService.open({
        title: this.translate.instant('Success'),
        message: this.translate.instant('Code copied to clipboard'),
        type: 'success',
        acceptText: this.translate.instant('OK'),
        showClose: true,
      });
    });
  }

  capitalizeFullNameClient(name: string): string {
    return name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
